import React, { useContext, useEffect, useRef, useState } from 'react';
import { Dialog } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import {
  selectInventorySelections,
  selectOutputVersions,
  selectSelectedHierarchy,
  selectVersionSelections
} from '@/redux/selectors/orderPlacementSelectors';
import { getProfiles } from '@/redux/slices/profileSlice';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import { FormikErrors, useFormikContext } from 'formik';
import { selectCustomersList } from '@/redux/selectors/constantsSelectors';
import { clearInventorySelections, clearOutputVersions, clearVersionSelections } from '@/redux/slices/orderPlacementSlice';
import Autocomplete from '../../_common/Form/Autocomplete';
import ProfileSelectionField from './ProfileSelectionField';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { CustomerProfile, FormJob, FormValues } from '@/redux/slices/slice_types';

interface Props {
  jobIndex: number;
  profilesByCustomerName: Record<string, CustomerProfile[]>;
  hasMultipleJobs?: boolean;
}

const OPCustomerProfileFields: React.FC<Props> = ({ jobIndex, profilesByCustomerName, hasMultipleJobs }) => {
  const dispatch = useAppDispatch();
  const { values, setFieldValue, errors, touched } = useFormikContext<FormValues>();
  const filterProfileList = useContext(ConfigContext)?.orderPlacementConfig?.filterProfileList;
  const filterCustomerList = useContext(ConfigContext)?.orderPlacementConfig?.filterCustomerList;
  const customersListFromStore = useAppSelector(selectCustomersList);
  const [customersList, setCustomersList] = useState<string[]>([]);

  const { jobs } = values;
  const job = jobs[jobIndex];
  const profiles = job.customer ? profilesByCustomerName[job.customer] : undefined;
  const [profileOptions, setProfileOptions] = useState<CustomerProfile[]>([]);
  const addedProfileType = values.jobs?.[0]?.profile?.data?.workflow_type || 'version_based';
  const shouldHideHiddenProfiles = useContext(ConfigContext)?.orderPlacementConfig?.shouldHideHiddenProfiles;
  const inventorySelections = useAppSelector(selectInventorySelections);
  const versionSelections = useAppSelector(selectVersionSelections);
  const outputVersions = useAppSelector(selectOutputVersions);
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const [isOpen, setIsOpen] = useState(false);
  const [newlySetProfile, setNewlySelectedProfile] = useState(null);
  const ProfileSelectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (filterCustomerList && hasMultipleJobs) {
      const filteredCustomers = filterCustomerList(customersListFromStore, selectedHierarchy);

      setCustomersList(filteredCustomers);
    } else {
      setCustomersList(customersListFromStore);
    }
  }, [customersListFromStore]);

  useEffect(() => {
    // if we only have one customer available, auto select it
    if (customersList.length === 1 && !job.customer) {
      setFieldValue(`jobs[${jobIndex}].customer`, customersList[0]);
    }
  }, [customersList]);

  useEffect(() => {
    // if we only have one profile available, auto select it
    if (profileOptions.length === 1 && !job.profile) {
      setFieldValue(`jobs[${jobIndex}].profile`, profileOptions[0]);
    }
  }, [profileOptions]);

  useEffect(() => {
    if (!job.customer) return;

    if (!profilesByCustomerName[job.customer]) {
      dispatch(
        getProfiles({
          customer: job.customer,
          order_on: 'name',
          direction: 'asc',
          ...(shouldHideHiddenProfiles ? { show_hidden: false } : {})
        })
      )
        .unwrap()
        .then((res) => {
          if (!res.profiles?.length) {
            return setFieldValue(`jobs[${jobIndex}].profile`, null);
          }
        });
    } else {
      // if we have a profile selected that doesn't exist in the new customer's profiles, clear it
      if (profilesByCustomerName[job.customer] && !profilesByCustomerName[job.customer]?.find((p) => p.id === job.profile?.id)) {
        setFieldValue(`jobs[${jobIndex}].profile`, null);
      }

      // Apply filterProfileList from config if available
      if (filterProfileList && selectedHierarchy && hasMultipleJobs) {
        const filteredProfiles = filterProfileList(profilesByCustomerName, job.customer, selectedHierarchy);
        setProfileOptions(filteredProfiles);
      } else {
        setProfileOptions(profilesByCustomerName[job.customer]);
      }

      // Apply filterCustomerList from config if available
      if (filterCustomerList && selectedHierarchy && hasMultipleJobs) {
        const filteredCustomers = filterCustomerList(customersListFromStore, selectedHierarchy);

        setCustomersList(filteredCustomers);
      } else {
        setCustomersList(customersListFromStore);
      }
    }
  }, [job.customer, selectedHierarchy]);

  useEffect(() => {
    const filterByVersionBased = values?.jobs?.length > 1;

    // when the customer is set, update the available profiles
    let newProfileOptions =
      profiles && job.profile
        ? // i know this looks insane but it works and is actually legit
          [...new Set(profiles.map((p) => JSON.stringify(p)))].map((p) => JSON.parse(p))
        : profiles || [];

    // only version based profiles can have multiple profiles added
    // if we have already added a profile, filter the profiles dropdown
    if (filterByVersionBased) {
      newProfileOptions = newProfileOptions.filter((p) => ['version_based', 'package', 'version_based_bundle'].indexOf(p.data?.workflow_type) !== -1);
    }

    setProfileOptions(newProfileOptions);
  }, [profiles, addedProfileType]);

  const onCustomerChange = (val: FormJob['customer']) => {
    setFieldValue(`jobs[${jobIndex}].customer`, val);
    setFieldValue(`jobs[${jobIndex}].profile`, null);
  };

  const isCustomerValid = Boolean(
    touched.jobs && touched.jobs[jobIndex]?.customer && errors.jobs && (errors.jobs as FormikErrors<FormJob>[])[jobIndex]?.customer
  );
  const isProfileValid = Boolean(
    touched.jobs && touched.jobs[jobIndex]?.profile && errors.jobs && (errors.jobs as FormikErrors<FormJob>[])[jobIndex]?.profile
  );

  return (
    <>
      <Dialog
        open={isOpen}
        title="Discard Selections and Change Profile"
        titleIcon={<WarningIcon icon="error" height={18} />}
        submitText="Yes, abandon changes"
        onSubmit={() => {
          setIsOpen(false);
          setFieldValue(`jobs[${jobIndex}].profile`, newlySetProfile);
          dispatch(clearVersionSelections());
          dispatch(clearInventorySelections());
          dispatch(clearOutputVersions());
          ProfileSelectRef.current?.blur();
        }}
        cancelText="No, stay here"
        maxWidth={480}
        onCancel={() => {
          setIsOpen(false);
          console.log('canceled');
        }}
      >
        Changing the profile selected will discard all previous file/version selections.
      </Dialog>
      <Grid item xs={12} sm={6} md={5} lg={4}>
        <Autocomplete
          id="customer"
          label="Customer"
          data-testid="customer-select"
          required
          options={customersList}
          value={job.customer || null}
          onChange={(el, val: FormJob['customer']) => onCustomerChange(val)}
          disabled={customersList?.length === 1}
          error={isCustomerValid}
        />
      </Grid>

      <Grid item xs={12} md={7}>
        <ProfileSelectionField
          innerRef={ProfileSelectRef}
          value={job.profile}
          options={profileOptions}
          onChange={(el, val) => {
            if (job.profile !== null && val && (inventorySelections.length > 0 || versionSelections.length > 0 || outputVersions.length > 0)) {
              setNewlySelectedProfile(val);
              setIsOpen(true);
            } else {
              setFieldValue(`jobs[${jobIndex}].profile`, val);
            }
          }}
          disabled={!job.customer}
          error={isProfileValid}
        />
      </Grid>
    </>
  );
};

export default OPCustomerProfileFields;

const WarningIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.warning.main};
  margin-right: 10px;
`;
